import PropTypes from "prop-types";
import { useState } from "react";
import { QuestionCircleOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import AddEditDateRange from "components/owner-app/modals/location-editor/AddEditDateRange";
import { BbotTable, Button, Checkbox, notification, Radio, Tooltip } from "top-component-library";
import { hasOverlap, withRange } from "../../dateRangeUtils";

// Legacy label variants: a menu that doesn't accept future orders restricts its
// plain "available" dates; one that does distinguishes ordering (visible
// schedule) from delivery/pickup (active schedule).
const restrictionKind = (scheduleType, allowOrderAhead) => {
  if (!allowOrderAhead) return "available";
  return scheduleType === "visible" ? "ordering" : "delivery/pickup";
};

const kindTitleCase = { available: "Available", ordering: "Ordering", "delivery/pickup": "Delivery/Pickup" };

const formatRange = (range) =>
  `${moment(range.start_date).format("MM/DD/YYYY hh:mm A")} to ${moment(range.end_date).format("MM/DD/YYYY hh:mm A")}`;

// Allow/exclude date-range lists for one schedule type. Edits update the menu's
// lists locally (onChange); they persist with the menu's Save button. Whitelist
// and blacklist are mutually exclusive, matching the legacy page.
const DateRangeRestrictions = ({ menu, scheduleType, onChange }) => {
  const whitelist = menu[`${scheduleType}_date_ranges_whitelist`] || [];
  const blacklist = menu[`${scheduleType}_date_ranges_blacklist`] || [];

  const [enabled, setEnabled] = useState(whitelist.length > 0 || blacklist.length > 0);
  const [restrictionType, setRestrictionType] = useState(blacklist.length > 0 ? "exclude" : "allow");
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const kind = restrictionKind(scheduleType, menu.allow_order_ahead);
  const restrictLabel = `Restrict ${kind} dates`;
  const restrictTooltip = `Enable this option to restrict ${kind} to specific dates.`;
  const currentList = restrictionType === "allow" ? whitelist : blacklist;

  const commitList = (list, type = restrictionType) =>
    type === "allow" ? onChange(scheduleType, list, []) : onChange(scheduleType, [], list);

  const toggleEnabled = (checked) => {
    setEnabled(checked);
    if (!checked) onChange(scheduleType, [], []);
  };

  const changeType = (nextType) => {
    const content = currentList;
    setRestrictionType(nextType);
    commitList(content, nextType);
  };

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (range, index) => {
    setEditing({ range, index });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSubmit = (startDate, endDate, notes) => {
    const candidate = withRange(
      currentList,
      { start_date: startDate, end_date: endDate, notes },
      editing ? editing.index : null
    );
    if (hasOverlap(candidate)) {
      notification.error({ message: "That date range overlaps an existing one. Please adjust the dates." });
      return;
    }
    commitList(candidate);
    closeModal();
  };

  const handleBulkDelete = (selectedKeys) => {
    commitList(currentList.filter((_, index) => !selectedKeys.includes(String(index))));
  };

  const columns = [
    { title: "Date Range", dataIndex: "dateRange", key: "dateRange" },
    { title: "Notes", dataIndex: "notes", key: "notes" },
    {
      title: "",
      key: "actions",
      render: (_value, record) => (
        <Button
          type="link"
          onClick={(event) => {
            event.stopPropagation();
            openEdit(record.range, record.index);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  const data = currentList.map((range, index) => ({
    key: String(index),
    dateRange: formatRange(range),
    notes: range.notes,
    range,
    index,
  }));

  return (
    <div className="margin-top-3">
      <Checkbox checked={enabled} onChange={(event) => toggleEnabled(event.target.checked)}>
        {restrictLabel}{" "}
        <Tooltip title={restrictTooltip}>
          <QuestionCircleOutlined style={{ padding: 4 }} />
        </Tooltip>
      </Checkbox>
      {enabled && (
        <div className="margin-top-2">
          <Radio.Group value={restrictionType} onChange={(event) => changeType(event.target.value)}>
            <Radio value="allow">Allow only these dates</Radio>
            <Radio value="exclude">Exclude these dates</Radio>
          </Radio.Group>
          <div className="margin-top-2">
            <BbotTable
              id={`${menu.menuId}-${scheduleType}-date-ranges`}
              title={`${restrictionType === "allow" ? "Allowed" : "Excluded"} ${kindTitleCase[kind]} Dates`}
              manuallyFormattedColumns={columns}
              data={data}
              allowBulkDeletion
              onBulkDelete={handleBulkDelete}
              onAdd={openAdd}
              addButtonText="Add Dates"
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
            />
          </div>
        </div>
      )}
      {modalOpen && (
        <AddEditDateRange
          show
          onHideCallback={closeModal}
          onSubmitCallback={handleSubmit}
          initialDateRange={editing ? { dateRange: editing.range, index: editing.index } : null}
        />
      )}
    </div>
  );
};

DateRangeRestrictions.propTypes = {
  menu: PropTypes.object.isRequired,
  scheduleType: PropTypes.oneOf(["active", "visible"]).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DateRangeRestrictions;
